import { ActionReference, DefaultGameState, GameObjectReference, GameState, CheckVisitedRoomsRequest } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { MapDisplayComponent } from "./MapDisplayComponent";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(35vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
    }

    .title {
        text-align: center;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: 100%;
        height: 100%;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 10px;
        padding: 0 10px;
    }

    /* The typing effect */
    @keyframes typing {
        from { width: 0; }
        to { width: 100%; }
    }

    .content p {
        margin: 0 0 10px 0;
        overflow: hidden; /* Ensures the content is not revealed until the animation */
        white-space: nowrap; /* Keeps the content on a single line */
        width: 0;
        animation: 
            typing 1.5s steps(40, end) forwards;
    }

    .content p:nth-child(2) {
        animation-delay: 0s;
    }

    .content p:nth-child(3) {
        animation-delay: 1.5s;
    }

    .content p:nth-child(4) {
        animation-delay: 3s;
    }

    .content p:nth-child(5) {
        animation-delay: 4.5s;
    }

    .content p:nth-child(6) {
        animation-delay: 6s;
    }

    .content p:nth-child(7) {
        animation-delay: 7.5s;
    }

    .content p:nth-child(8) {
        animation-delay: 9s;
    }

   .footer {
        background-color:rgb(15, 77, 163);
        border: 1px solid #332c57;
        margin-top: -22px;
        margin-bottom: 3px;
        display: flex;
        height: 150px;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding: 10px 10px 0 10px;
    }

    .footer .button {
        background-color: rgb(29, 120, 246);
        border: 1px solid #332c57;
        padding: 5px 10px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    .footer .button.active,
    .footer .button:hover {
        background-color: rgb(8, 43, 91);
    }

    #toggle-inventory {
        background-color: rgb(86, 157, 255);
    }
    
    #toggle-inventory.active {
        background-color: rgb(44, 102, 182);
    }

    #toggle-inventory:hover {
        background-color: rgb(0, 106, 255);
    }

   .pixelCorners {
        clip-path: polygon(
        0px calc(100% - 8px),
        4px calc(100% - 8px),
        4px calc(100% - 4px),
        8px calc(100% - 4px),
        8px 100%,
        calc(100% - 8px) 100%,
        calc(100% - 8px) calc(100% - 4px),
        calc(100% - 4px) calc(100% - 4px),
        calc(100% - 4px) calc(100% - 8px),
        100% calc(100% - 8px),
        100% 8px,
        calc(100% - 4px) 8px,
        calc(100% - 4px) 4px,
        calc(100% - 8px) 4px,
        calc(100% - 8px) 0px,
        8px 0px,
        8px 4px,
        4px 4px,
        4px 8px,
        0px 8px
        );
   }

`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        window.addEventListener("toggle-element", e => {
            if (e instanceof CustomEvent) {
                this.toggle((e.detail as string));
            }
        }, false);
        window.addEventListener("handle-action", async e => {
            if (e instanceof CustomEvent) {
                await this.handleAction((e.detail as string));
            }
        }, false);
        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
            <map-display id="map-display"></map-display>
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content">
                <game-inventory></game-inventory>
                ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
            </div>
        `;
    }

    private renderMapButton(): HTMLElement {
        if (this._currentGameState && this._currentGameState.roomAlias !== "startup" && this._currentGameState.roomAlias !== "ending") {
            const element: HTMLElement = html`
                <a class="button pixelCorners" id="check-map">Map</a>
            `;
            element.addEventListener("click", () => this.handleMapCheck()); // Ensure event listener is here
            return element;
        }

        return html`<div></div>`;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement | string {
        return html`
            <div class="footer pixelCorners">
                <div class="keypad" style="display: none;">
                    ${this.renderKeypad()}
                </div>
                <div class="buttons">
                    <div>
                        ${this.renderInventoryButton()}
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                        ${this.renderMapButton()}
                    </div>
                    <div>
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>
            </div>
        `;
    }

    private async handleMapCheck(): Promise<void> {
        // retrieve the player's visited rooms from the game route service.
        const visitedRooms: string[] = await this._gameRouteService.getVisitedRooms();
        // send the visited rooms to check
        const result: CheckVisitedRoomsRequest | undefined = await this._gameRouteService.checkVisitedRooms(visitedRooms);
        const mapDisplay: MapDisplayComponent = this.shadowRoot!.querySelector(
            "#map-display"
        ) as MapDisplayComponent;
        // Check if the  response success.
        if (result && result.success) {
            mapDisplay.setMessage(result.message);
        }
        else if (result && result.missingRooms) {
            // If there are missing rooms, set the message to indicate which rooms are missing.
            mapDisplay.setMessage(
                (typeof result.message === "string" ? result.message : "Unknown message") +
                "<br><br>Missing rooms:<br>" +
                (Array.isArray(result.missingRooms) ? result.missingRooms.join("<br>") : "No missing rooms")
            );
        }
        else if (result) {
            // If there's a general message display it.
            mapDisplay.setMessage(result.message);
        }
        else {
            // If there's no response  or an error occurred display an error message
            mapDisplay.setMessage("An error occurred while checking the map.");
        }

        mapDisplay.show();
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button pixelCorners ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render an inventory button
     *
     * @returns HTML element of the inventory button
     */
    private renderInventoryButton(): HTMLElement {
        if (this._currentGameState && this._currentGameState.roomAlias !== "startup" && this._currentGameState.roomAlias !== "ending") {
            const element: HTMLElement = html`
                <a class="button pixelCorners" id="toggle-inventory">
                    Inventory
                </a>
            `;

            element.addEventListener("click", () => {
                window.dispatchEvent(new CustomEvent("toggle-component", { detail: "inventory" }));
                element.classList.toggle("active");
            });

            return element;
        }

        return html`<div></div>`;
    }

    /**
     * Render a keypad button
     *
     * @returns HTML element of the keypad button
     */
    private renderKeypad(): HTMLElement {
        const element: HTMLElement = html`
            <game-keypad lock-id="${this._currentGameState?.roomAlias}"></game-keypad>
        `;

        return element;
    }

    /**
     * Toggle a element by querySelector
     */
    private toggle(e: string): void {
        const element: HTMLElement | null | undefined = this.shadowRoot?.querySelector(e);

        if (element) {
            if (element.style.display === "none") {
                element.style.display = "block";
                return;
            }
            element.style.display = "none";
        }
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button pixelCorners ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (button.alias === "restart-game") {
            localStorage.clear();
            window.location.reload();
            return;
        }

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    private async handleAction(action: string): Promise<void> {
        const splitString: string[] = action.split(":");
        const state: GameState | undefined = await this._gameRouteService.executeAction(splitString[0], [splitString[1]]);
        this.render();
        if (state !== undefined) {
            this.updateGameState(state);
        }
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        if (button.alias === "keypad" && this._selectedActionButton.alias === "use") {
            window.dispatchEvent(new CustomEvent("toggle-element", { detail: ".keypad" }));
            window.dispatchEvent(new CustomEvent("toggle-element", { detail: ".buttons" }));
            return;
        }
        console.log(button, this._selectedActionButton.alias);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
